import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  styled,
} from "@mui/material";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";

import {
  TodaysChallenges,
  ProTipStories,
  Podcast,
  Gameplay,
  // GameFeatures,
  News,
  HomeFooter,
} from "./HomeSections";

import { Swiper, SwiperSlide } from "swiper/react";

import SwiperCore from "swiper";

import "swiper/css";
import "swiper/css/autoplay";

import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css/pagination";

import { getAllGames } from "../../apiCalls/games/api_v1_games";
import {
  claimDailyStreak,
  loadActiveStreak,
} from "../../apiCalls/streaks/api_v1_streaks";

import { useNavigate, useLocation, useNavigationType } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import { AuthContext } from "../../apiCalls/jwt/AuthContext";

import CallofDuty from "../../assets/img/news/img_callofduty.avif";
import Fortnite from "../../assets/img/news/img_fortnite.avif";
import Valorant from "../../assets/img/news/img_valorant.avif";
import Freefire from "../../assets/img/news/img_freefire.avif";
import Minecraft from "../../assets/img/news/img_minecraft.avif";
import Pubg from "../../assets/img/news/img_pubg.avif";
import { hasSeenIntro } from "../intro/introStorage";
import { useSkillLevel } from "../../components/ui/GaPrimitives";
import {
  DailyStreakDialog,
  getHomeStreakState,
} from "../../components/ui/GaDialogs";

import { trackGameSelect } from "../../utils/analytics";

SwiperCore.use([Autoplay, Pagination]);

const HeroCard = styled(Card)(({ theme }) => ({
  position: "relative",

  width: "100%",
  maxWidth: "100%",
  height: "221px",
  margin: "18px auto 0",
  background: "#15170F",
  borderRadius: 0,
  clipPath: "polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%)",
  overflow: "hidden",

  "& .hero-content": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  "& .background-shape": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    clipPath: "polygon(0 0, 62% 0, 38% 100%, 0 100%)",

    background: "linear-gradient(135deg, #B8EE3C 0%, #6FA31A 100%)",
    zIndex: 0,
  },
}));

const ContentWrapper = styled(Box)({
  position: "relative",
  padding: "1em",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  zIndex: 2,
});

const StyledTitle = styled(Typography)({
  fontFamily: "'Rajdhani', sans-serif",
  fontSize: "32px",
  fontWeight: 700,
  letterSpacing: "1.5px",
  color: "#0D0F0A",
  marginBottom: "8px",
  lineHeight: 1.05,
});

const CharacterImage = styled("div")({
  position: "absolute",
  right: "-2%",
  top: "80%",
  transform: "translateY(-50%)",
  height: "120%",
  width: "50%",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right",
  zIndex: 2,
});

function syncDesktopBannerCards(swiper) {
  if (!swiper?.slides?.length) return;
  const perView =
    typeof swiper.params.slidesPerView === "number"
      ? swiper.params.slidesPerView
      : 1;

  swiper.slides.forEach((slide) => {
    const card = slide.querySelector("[data-banner-card]");
    if (!card) return;
    const progress = typeof slide.progress === "number" ? slide.progress : 0;
    const minProgress = -(perView - 1);
    let outside = 0;
    if (progress > 0.02) outside = progress;
    else if (progress < minProgress - 0.02) outside = minProgress - progress;
    const t = Math.min(outside, 1);
    const blur = t * 8;
    const opacity = 1 - t * 0.5;
    const scale = 1 - t * 0.08;
    const focused = t < 0.04;

    card.style.filter = blur < 0.25 ? "none" : `blur(${blur.toFixed(2)}px)`;
    card.style.opacity = opacity.toFixed(3);
    card.style.transform = `scale(${scale.toFixed(3)})`;
    card.style.borderColor = focused
      ? "rgba(255,255,255,0.55)"
      : "rgba(255,255,255,0.14)";
    card.style.boxShadow = focused
      ? "0 18px 36px rgba(0,0,0,0.48), 0 0 28px rgba(184,238,60,0.18)"
      : "0 8px 16px rgba(0,0,0,0.28)";
  });
}

function Home() {
  const { auth, fetchNewRefreshToken } = useContext(AuthContext);
  const [games, setGames] = useState([]);
  const storedSkillLevel = useSkillLevel();
  const [heroBanners, setHeroBanners] = useState([]);
  const [streakOpen, setStreakOpen] = useState(false);
  const [streakClaimed, setStreakClaimed] = useState(false);
  const [streakDay, setStreakDay] = useState(1);
  const [claimAmount, setClaimAmount] = useState(1);
  const [claimingStreak, setClaimingStreak] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const isDesktop = useMediaQuery("(min-width:900px)", { noSsr: true });
  const isAuthenticated = Boolean(auth.accessToken);

  useEffect(() => {
    const scrollContainer = document.getElementById("main-scroll-container");
    if (!scrollContainer) return;

    if (navType === "POP") {
      const savedScroll = sessionStorage.getItem("homeScrollPosition");
      if (savedScroll) {
        const targetScroll = parseInt(savedScroll, 10);
        scrollContainer.scrollTo(0, targetScroll);

        if (scrollContainer.scrollTop < targetScroll) {
          const contentToObserve = scrollContainer.firstElementChild || scrollContainer;
          const observer = new ResizeObserver(() => {
            if (scrollContainer.scrollHeight >= targetScroll) {
              scrollContainer.scrollTo(0, targetScroll);
              observer.disconnect();
            }
          });
          observer.observe(contentToObserve);
          const timer = setTimeout(() => observer.disconnect(), 2500);
          return () => {
            observer.disconnect();
            clearTimeout(timer);
          };
        }
      }
    } else {
      scrollContainer.scrollTo(0, 0);
    }
  }, [navType]);

  useEffect(() => {
    const scrollContainer = document.getElementById("main-scroll-container");
    if (!scrollContainer) return;

    const handleClick = () => {
      // Save exact scroll position the moment the user clicks anything on the home page
      sessionStorage.setItem("homeScrollPosition", scrollContainer.scrollTop.toString());
    };
    
    scrollContainer.addEventListener("click", handleClick, true);
    return () => scrollContainer.removeEventListener("click", handleClick, true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBanners = async () => {
      if (isDesktop) {
        const mod = await import("./desktopBanners");
        if (!cancelled) setHeroBanners(mod.DESKTOP_BANNERS);
      } else {
        const mod = await import("./mobileBanners");
        if (!cancelled) setHeroBanners(mod.HOME_BANNERS);
      }
    };
    loadBanners();
    return () => {
      cancelled = true;
    };
  }, [isDesktop]);

  useEffect(() => {
    // Guests (no access token): stay on home — no intro, no streak popup.
    if (!isAuthenticated) {
      setStreakOpen(false);
      return undefined;
    }

    if (!hasSeenIntro()) {
      navigate("/intro", { replace: true });
      return undefined;
    }

    let cancelled = false;
    const state = getHomeStreakState();
    setStreakDay(state.streakDay);
    setStreakClaimed(state.claimed);

    (async () => {
      let claimed = state.claimed;
      let day = state.streakDay;
      let amount = 1;
      try {
        const streak = await loadActiveStreak({ force: true });
        if (streak) {
          claimed = streak.rewardClaimed;
          day = streak.displayDay;
          amount = streak.amountToClaim || 1;
          if (streak.rewardClaimed) {
            sessionStorage.setItem(`ga_streak_claimed_${state.today}`, "1");
          } else {
            sessionStorage.removeItem(`ga_streak_claimed_${state.today}`);
          }
        }
      } catch (error) {
        console.error("Active streak load failed:", error);
      }
      if (cancelled) return;
      setStreakClaimed(claimed);
      setStreakDay(day);
      setClaimAmount(amount);
      if (!state.shown) {
        sessionStorage.setItem(`ga_streak_shown_${state.today}`, "1");
        // Only open when the daily egg can still be claimed — never the
        // "TODAY'S EGG ALREADY CLAIMED" modal on home (mobile + desktop).
        if (!claimed) setStreakOpen(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    const checkMsisdn = async () => {
      const urlParams = new URLSearchParams(location.search);
      const msisdn = urlParams.get("a");

      if (msisdn && !auth.accessToken) {
        try {
          await fetchNewRefreshToken(msisdn);
        } catch (error) {
          console.error("Error authenticating with msisdn:", error);
        }
      }
    };

    checkMsisdn();
  }, [location, auth.accessToken, fetchNewRefreshToken]);

  const getGameImage = (gameName) => {
    switch (gameName) {
      case "Fortnite":
        return Fortnite;
      case "PUBG":
        return Pubg;
      case "Call of Duty":
        return CallofDuty;
      case "Minecraft":
        return Minecraft;
      case "Valorant":
        return Valorant;
      case "Freefire":
        return Freefire;
      default:
        return null;
    }
  };

  const handleBannerClick = (path) => {
    if (!path) return;
    navigate(path);
  };

  useEffect(() => {
    document.title = "Home | Gamez Academy";

    const fetchGames = async () => {
      try {
        const response = await getAllGames({ useCache: false });
        if (response.data.success) {
          setGames(response.data.data.games);
        }
      } catch (error) {
        console.error("Error fetching games:", error);
      }
    };

    fetchGames();
  }, []);

  useEffect(() => {
    if (swiperInstance && games.length > 1) {
      swiperInstance.autoplay.start();
    }
  }, [swiperInstance, games]);

  const handleGameClick = (uuid, gameName) => {
    trackGameSelect(gameName, uuid);

    if (isAuthenticated) {
      navigate(`/game?uuid=${uuid}&game=${gameName}`);
    } else {
      const urlParams = new URLSearchParams(location.search);
      const msisdn = urlParams.get("a");
      const loginUrl = msisdn
        ? `${process.env.REACT_APP_LOGIN_URL}?a=${msisdn}`
        : process.env.REACT_APP_LOGIN_URL;
      const refferer =
        sessionStorage.getItem("refferer") ||
        urlParams.get("referer") ||
        urlParams.get("refferer");
      if (refferer == "gamenow") {
        return;
      } else {
      }
    }
  };

  return (
    <Box
      sx={{
        maxWidth: "100%",
        width: "100%",
        bgcolor: "#0D0F0A",
        fontFamily: "'Poppins', sans-serif",
        px: { xs: "16px", md: "20px" },
        boxSizing: "border-box",
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            width: "100%",
            px: 0,
            pt: "16px",
            pb: 0,
            "& .swiper": {
              padding: "10px 2px 22px",
              overflow: "hidden",
            },
            "@media (min-width: 1100px) and (max-width: 1279.98px)": {
              "& .swiper": {
                maxWidth: 1080,
                mx: "auto",
              },
            },
            "@media (min-width: 1280px)": {
              "& .swiper": {
                maxWidth: 1560,
                mx: "auto",
              },
            },
            "& .swiper-slide": {
              height: "auto",
            },
            "& .swiper-pagination": {
              bottom: "0px !important",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "5px",
            },
            "& .swiper-pagination-bullet": {
              bgcolor: "#2A2E22",
              width: 7,
              height: 3,
              borderRadius: 0,
              opacity: 1,
              margin: "0 !important",
            },
            "& .swiper-pagination-bullet-active": {
              bgcolor: "#B8EE3C",
              width: 28,
            },
          }}
        >
          <Swiper
            slidesPerView={1}
            spaceBetween={14}
            watchSlidesProgress
            autoplay={{
              delay: 2800,
              disableOnInteraction: false,
            }}
            loop={heroBanners.length > 1}
            pagination={{ clickable: true }}
            modules={[Autoplay, Pagination]}
            speed={680}
            breakpoints={{
              0: { slidesPerView: 1, spaceBetween: 14 },
              1100: { slidesPerView: 2, spaceBetween: 16 },
              1280: { slidesPerView: 3, spaceBetween: 18 },
            }}
            onSwiper={(swiper) => {
              setSwiperInstance(swiper);
              syncDesktopBannerCards(swiper);
            }}
            onProgress={syncDesktopBannerCards}
            onResize={syncDesktopBannerCards}
            onBreakpoint={syncDesktopBannerCards}
          >
            {heroBanners.map((banner, bannerIndex) => (
              <SwiperSlide
                key={banner.key}
                onClick={() => handleBannerClick(banner.path)}
                style={{
                  cursor: banner.path ? "pointer" : "default",
                  height: "auto",
                }}
              >
                <Box
                  data-banner-card
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "960 / 534",
                    overflow: "hidden",
                    bgcolor: "#15170F",
                    borderRadius: "18px",
                    border: "1px solid rgba(255,255,255,0.55)",
                    boxShadow:
                      "0 18px 36px rgba(0,0,0,0.48), 0 0 28px rgba(184,238,60,0.18)",
                    transformOrigin: "center center",
                    willChange: "transform, opacity, filter",
                  }}
                >
                  <Box
                    component="img"
                    src={banner.image}
                    alt=""
                    loading={bannerIndex === 0 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={bannerIndex === 0 ? "high" : "low"}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            mb: 0,
            px: 0,
            "& .swiper": {
              paddingBottom: "16px",
            },
            "& .swiper-pagination": {
              bottom: "0px !important",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "5px",
            },
            "& .swiper-pagination-bullet": {
              width: 7,
              height: 3,
              margin: "0 !important",
              borderRadius: 0,
              bgcolor: "#2A2E22",
              opacity: 1,
            },
            "& .swiper-pagination-bullet-active": {
              width: 28,
              bgcolor: "#B8EE3C",
            },
          }}
        >
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
            }}
            loop={heroBanners.length > 1}
            pagination={{ clickable: true }}
            modules={[Autoplay, Pagination]}
            onSwiper={setSwiperInstance}
            speed={800}
          >
            {heroBanners.map((banner, bannerIndex) => (
              <SwiperSlide
                key={banner.key}
                onClick={() => handleBannerClick(banner.path)}
                style={{ cursor: banner.path ? "pointer" : "default" }}
              >
                <HeroCard
                  elevation={0}
                  sx={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "2272 / 1264",
                    background: "#0D0F0A",
                  }}
                >
                  <Box
                    component="img"
                    src={banner.image}
                    alt=""
                    loading={bannerIndex === 0 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={bannerIndex === 0 ? "high" : "low"}
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </HeroCard>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      )}
      <Box
        sx={{
          display: "block",
        }}
      >
        <TodaysChallenges games={games} skillLevel={storedSkillLevel} />
        <ProTipStories skillLevel={storedSkillLevel} />
        <Podcast skillLevel={storedSkillLevel} />
        <Gameplay skillLevel={storedSkillLevel} />
        {/* <GameFeatures games={games} /> */}
        <News games={games} hideFooter />
      </Box>
      <HomeFooter />
      <DailyStreakDialog
        open={streakOpen}
        claimed={streakClaimed}
        streakDay={streakDay}
        claimAmount={claimAmount}
        claiming={claimingStreak}
        onClose={() => setStreakOpen(false)}
        onPlay={() => {
          setStreakOpen(false);
          navigate("/challenges");
        }}
        onClaim={async () => {
          if (claimingStreak || streakClaimed) return;
          setClaimingStreak(true);
          try {
            const streak = await claimDailyStreak();
            if (!streak?.success) {
              const refreshed = await loadActiveStreak({ force: true });
              if (refreshed?.rewardClaimed) {
                setStreakClaimed(true);
                setStreakDay(refreshed.displayDay);
                setStreakOpen(false);
              }
              return;
            }
            const today = new Date().toISOString().slice(0, 10);
            sessionStorage.setItem(`ga_streak_claimed_${today}`, "1");
            setStreakClaimed(true);
            setStreakDay(streak.displayDay);
            setClaimAmount(streak.eggsAwarded || claimAmount);
            setStreakOpen(false);
          } catch (error) {
            console.error("Daily streak update failed:", error);
          } finally {
            setClaimingStreak(false);
          }
        }}
      />
    </Box>
  );
}

export default Home;

